/**
 * Biblioteca de operações com o CorelDraw
 * Usa a classe CorelCommunication para comunicação base
 */

import { corelCommunication } from './corel/communication';
import { toCm, Unit } from './units';
import { 
  WebViewResponse, 
  OrganizedObject, 
  ApiOrganizedObject, 
  OrderObject 
} from './corel/types/messages';

// Chave usada para armazenar os dados (migrada para padrão central)
import { storage } from './storage/storage';
import { sendToTerminal, terminalLog } from './terminalLogger';
import { Keys, readWithAliases } from './storage/keys';

// ✅ Serviços centralizados
import { LayerService } from './corel/actions/layer';
import { MetadataService } from './corel/actions/metadata';
import type { UpsertItem } from './corel/actions/metadata.types';

/**
 * Interface para objeto com multiplicador utilizado nas operações de nomeação/atualização.
 * Representa um objeto do CorelDraw com informações de nome base, multiplicador e contração.
 */
export interface ObjetoMultiplicador {
  Nome: string;
  NomeBase: string;
  Multiplicador: number;
  NovoNome: string;
  Contracao: string;
  UsaContracaoVariavel: boolean;
}

/**
 * Objeto com multiplicador para atualização por StaticID (novo)
 */
export interface ObjetoMultiplicadorPorStaticID {
  staticId: string;
  multiplicador: number;
  nomeBase: string;
  contracao?: string;
  usaContracaoVariavel?: boolean;
}

// Interface para um objeto atualizado
export interface ObjetoAtualizado {
  id: number;
  nomeAntigo: string;
  nomeNovo: string;
  multiplicador: number;
}

/**
 * Nomeia objetos no documento CorelDraw ativo
 */
export const nomeadorDeObjetos = (): string | null => {
  return corelCommunication.sendActionGuarded('nomear-objetos', { idempotencyKey: 'rename|current' });
};

/**
 * Organiza objetos em uma camada no CorelDraw
 * 
 * @deprecated Use LayerService.getInstance().organizarCamada() ou LayerService.organizarCamadaAwait() diretamente.
 * Esta função foi mantida apenas para compatibilidade e delega para o serviço centralizado.
 */
export const layerOrganizador = (): string | null => {
  // ✅ DELEGAR para serviço centralizado (import estático)
  return LayerService.getInstance().organizarCamada();
};

/**
 * Executa organizar-camada e aguarda a resposta, retornando objetos normalizados mínimos
 * necessários para a distribuição por página (StaticID e Guid).
 * 
 * @deprecated Use LayerService.organizarCamadaAwait() diretamente.
 * Esta função foi mantida apenas para compatibilidade e delega para o serviço centralizado.
 */
export const organizarCamadaAwait = async (): Promise<{
  ok: boolean;
  objetos: Array<{ StaticID: string; Guid: string }>
}> => {
  // ✅ DELEGAR para serviço centralizado (import estático)
  return LayerService.organizarCamadaAwait();
};


/**
 * lock-document: trava/destrava o documento durante operações críticas (ex.: salvar no Docker)
 * Fluxo recomendado: parar monitor → iniciar lock → (renderizar checklist) → parar lock → salvar
 */
export const lockDocument = {
  iniciar: async (motivo: string = 'salvando'): Promise<{ ok: boolean; locked: boolean; code?: string }> => {
    return new Promise((resolve) => {
      try {
        const rid = corelCommunication.sendActionGuarded('lock-document', { comando: 'iniciar', motivo } as any, (response) => {
          try {
            // Validação: verificar se response é válido
            if (!response || typeof response !== 'object') {
              terminalLog.error('[LOCK_DOCUMENT] Resposta inválida ao iniciar lock', { motivo, responseType: typeof response });
              resolve({ ok: false, locked: false });
              return;
            }
            
            const status = (response as any)?.status;
            const result = (response as any)?.result || {};
            const ok = status === 'ok' && (result?.locked === true || result?.sucesso === true);
            const code = result?.code || (response as any)?.code;
            resolve({ ok, locked: !!result?.locked, code });
          } catch (err) {
            terminalLog.error('[LOCK_DOCUMENT] Erro ao processar resposta de iniciar lock', {
              motivo,
              error: err instanceof Error ? err.message : String(err)
            });
            resolve({ ok: false, locked: false });
          }
        });
        if (!rid) {
          terminalLog.error('[LOCK_DOCUMENT] Falha ao enviar mensagem de iniciar lock', { motivo });
          resolve({ ok: false, locked: false });
        }
      } catch (err) {
        terminalLog.error('[LOCK_DOCUMENT] Erro ao enviar mensagem de iniciar lock', {
          motivo,
          error: err instanceof Error ? err.message : String(err)
        });
        resolve({ ok: false, locked: false });
      }
    });
  },
  parar: async (): Promise<{ ok: boolean; locked: boolean; code?: string }> => {
    return new Promise((resolve) => {
      try {
        const rid = corelCommunication.sendActionGuarded('lock-document', { comando: 'parar' } as any, (response) => {
          try {
            // Validação: verificar se response é válido
            if (!response || typeof response !== 'object') {
              terminalLog.error('[LOCK_DOCUMENT] Resposta inválida ao parar lock', { responseType: typeof response });
              resolve({ ok: false, locked: true });
              return;
            }
            
            const status = (response as any)?.status;
            const result = (response as any)?.result || {};
            const ok = status === 'ok' && (result?.locked === false || result?.sucesso === true);
            const code = result?.code || (response as any)?.code;
            resolve({ ok, locked: !!result?.locked, code });
          } catch (err) {
            terminalLog.error('[LOCK_DOCUMENT] Erro ao processar resposta de parar lock', {
              error: err instanceof Error ? err.message : String(err)
            });
            resolve({ ok: false, locked: true });
          }
        });
        if (!rid) {
          terminalLog.error('[LOCK_DOCUMENT] Falha ao enviar mensagem de parar lock');
          resolve({ ok: false, locked: true });
        }
      } catch (err) {
        terminalLog.error('[LOCK_DOCUMENT] Erro ao enviar mensagem de parar lock', {
          error: err instanceof Error ? err.message : String(err)
        });
        resolve({ ok: false, locked: true });
      }
    });
  },
  status: async (): Promise<{ ok: boolean; locked: boolean; code?: string }> => {
    return new Promise((resolve) => {
      try {
        const rid = corelCommunication.sendActionGuarded('lock-document', { comando: 'status' } as any, (response) => {
          try {
            // Validação: verificar se response é válido
            if (!response || typeof response !== 'object') {
              terminalLog.error('[LOCK_DOCUMENT] Resposta inválida ao verificar status do lock', { responseType: typeof response });
              resolve({ ok: false, locked: false });
              return;
            }
            
            const status = (response as any)?.status;
            const result = (response as any)?.result || {};
            const ok = status === 'ok';
            const code = result?.code || (response as any)?.code;
            resolve({ ok, locked: !!result?.locked, code });
          } catch (err) {
            terminalLog.error('[LOCK_DOCUMENT] Erro ao processar resposta de status do lock', {
              error: err instanceof Error ? err.message : String(err)
            });
            resolve({ ok: false, locked: false });
          }
        });
        if (!rid) {
          terminalLog.error('[LOCK_DOCUMENT] Falha ao enviar mensagem de status do lock');
          resolve({ ok: false, locked: false });
        }
      } catch (err) {
        terminalLog.error('[LOCK_DOCUMENT] Erro ao enviar mensagem de status do lock', {
          error: err instanceof Error ? err.message : String(err)
        });
        resolve({ ok: false, locked: false });
      }
    });
  }
};

/**
 * Lista as camadas disponíveis no documento CorelDraw
 */
export const listarCamadas = (): string | null => {
  return corelCommunication.sendActionGuarded('listar-camadas', {});
};

/**
 * Foca em um objeto específico no CorelDraw pelo seu StaticID
 * @param staticId StaticID único do objeto para focar
 * @param callback Função opcional para receber a resposta do comando
 */
export const focarObjeto = (
  staticId: string, 
  callback?: (response: WebViewResponse) => void
): string | null => {
  // Certificar-se de que temos um StaticID válido
  if (!staticId || staticId.trim() === '') {
    sendToTerminal('⚠️ [FOCUS] StaticID inválido', { staticId });
    return null;
  }
  
  // Enviar o comando para o C# usando a nova action
  const payload = { staticId: staticId.trim(), idempotencyKey: `focus|${staticId.trim()}` } as any;
  const requestId = corelCommunication.sendActionGuarded('focar-objeto-por-staticid', payload, (response) => {
    if (response?.status !== 'ok') {
      sendToTerminal('❌ [FOCUS] Erro ao focar objeto', {
        status: response?.status,
        message: (response as any)?.message ?? (response as any)?.result?.mensagem
      });
    }
    try { if (callback) callback(response); } catch {}
  });
  
  if (!requestId) {
    sendToTerminal('❌ [FOCUS] Falha ao enviar comando', {});
  }
  
  return requestId;
};

/**
 * Extrai o nome base de um objeto
 */
export const extrairNomeBaseObjeto = (nomeCompleto: string): string => {
  if (!nomeCompleto) return '';
  let base = nomeCompleto.trim();
  // Remover contração no final (0px, 1px, 2px, 3px, AUTO, CV, VARIÁVEL)
  base = base.replace(/_(0px|1px|2px|3px|auto|cv|variável)$/i, '');
  // Remover multiplicador no final (_Xn)
  base = base.replace(/_X\d+$/i, '');
  // Caso a ordem seja invertida (ex.: _X2_0px), repetir a limpeza
  base = base.replace(/_(0px|1px|2px|3px|auto|cv|variável)$/i, '');
  base = base.replace(/_X\d+$/i, '');
  return base;
};

/**
 * Gera um nome de objeto com multiplicador
 */
export const gerarNomeComMultiplicador = (nomeBase: string, multiplicador: number): string => {
  return `${nomeBase}_X${multiplicador}`;
};

/**
 * Gera um nome de objeto com multiplicador e contração
 */
export const gerarNomeComMultiplicadorEContracao = (
  nomeBase: string, 
  multiplicador: number,
  contraction: string
): string => {
  return `${nomeBase}_X${multiplicador}_${contraction}`;
};

/**
 * Verifica se existe mais de um valor de contração nos objetos
 */
export const temContracoesVariaveis = (objects: any[]): boolean => {
  if (!objects || objects.length <= 1) return false;
  const firstContraction = objects[0].contraction || '0px';
  return objects.some(obj => (obj.contraction || '0px') !== firstContraction);
};

/**
 * Atualiza o multiplicador de um único objeto (legado - usa nome)
 */
export const atualizarMultiplicadorUnico = (
  nomeObjeto: string, 
  multiplicador: number,
  _callback?: (objetos: ObjetoAtualizado[]) => void,
  usaContracaoVariavel: boolean = false,
  contracao?: string
): string | null => {
  const nomeBase = extrairNomeBaseObjeto(nomeObjeto);
  const novoNome = usaContracaoVariavel && contracao
    ? gerarNomeComMultiplicadorEContracao(nomeBase, multiplicador, contracao)
    : gerarNomeComMultiplicador(nomeBase, multiplicador);

  const objetoComMultiplicador: ObjetoMultiplicador = {
    Nome: nomeObjeto,
    NomeBase: nomeBase,
    Multiplicador: multiplicador,
    NovoNome: novoNome,
    Contracao: contracao === "VARIÁVEL" ? "0px" : (contracao || "0px"),
    UsaContracaoVariavel: usaContracaoVariavel
  };
  
  return corelCommunication.sendActionGuarded('atualizar-multiplicadores-com-contracao', {
    objetos: [objetoComMultiplicador]
  });
};

/**
 * Atualiza metadados de múltiplos objetos em batch (NOVO - otimizado)
 * 
 * @param updates - Array de objetos com staticId, guid e campos a atualizar
 * @param callback - Callback opcional para processar resposta
 * @returns Promise com resultado do batch update
 */
export const updateObjectMetadataBatch = async (
  updates: Array<{
    staticId: string;
    guid: string;
    multiplicador?: number;
    contracao?: string;
    baseName?: string;
  }>,
  callback?: (result: { sucesso: boolean; updated: number; errors: any[]; items: any[] }) => void
): Promise<{ sucesso: boolean; updated: number; errors: any[] }> => {
  if (!updates || updates.length === 0) {
    try { sendToTerminal('❌ [META:BATCH] Empty updates array'); } catch {}
    if (callback) callback({ sucesso: false, updated: 0, errors: [], items: [] });
    return { sucesso: false, updated: 0, errors: [] };
  }

  // Validar que todos têm staticId e guid
  const invalid = updates.filter(u => !u.staticId || !u.guid);
  if (invalid.length > 0) {
    try { sendToTerminal('❌ [META:BATCH] Invalid params', { invalidCount: invalid.length }); } catch {}
    if (callback) callback({ sucesso: false, updated: 0, errors: [], items: [] });
    return { sucesso: false, updated: 0, errors: [] };
  }

  const items: UpsertItem[] = updates.map(u => ({
    staticId: u.staticId,
    guid: u.guid,
    multiplicador: u.multiplicador ?? 1, // Default se não especificado
    ...(typeof u.contracao === 'string' ? { contracao: u.contracao } : {}),
    ...(u.baseName ? { baseName: u.baseName } : {})
  }));

  try {
    sendToTerminal('📤 [META:BATCH] Enviando batch update', { count: items.length });
    const result = await MetadataService.upsert(items);
    
    sendToTerminal('✅ [META:BATCH] Batch update concluído', {
      updated: result.updated,
      errors: result.errors.length,
      total: items.length
    });

    // Normalizar resposta para callback
    const normalizedItems = items.map((it) => ({
      staticId: it.staticId,
      guid: it.guid,
      multiplicador: it.multiplicador,
      contracao: it.contracao ?? null,
      baseName: it.baseName
    }));

    if (callback) {
      callback({
        sucesso: result.sucesso,
        updated: result.updated,
        errors: result.errors,
        items: normalizedItems
      });
    }
    
    return {
      sucesso: result.sucesso,
      updated: result.updated,
      errors: result.errors
    };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    try {
      sendToTerminal('❌ [META:BATCH] Exception', { error: errorMsg });
    } catch {}
    if (callback) callback({ sucesso: false, updated: 0, errors: [{ message: errorMsg }], items: [] });
    return { sucesso: false, updated: 0, errors: [{ message: errorMsg }] };
  }
};

/**
 * Atualiza o multiplicador de um único objeto por StaticID (legado - mantido para compatibilidade)
 * 
 * @deprecated Para múltiplos objetos, use updateObjectMetadataBatch
 */
export const atualizarMultiplicadorUnicoPorStaticID = async (
  staticId: string,
  multiplicador: number,
  nomeBase: string,
  callback?: (objetos: any[], meta?: { status?: string; itens?: any[]; original?: any }) => void,
  _usaContracaoVariavel: boolean = false, // Mantido para compatibilidade, não usado internamente
  contracao?: string,
  guid?: string
): Promise<string | null> => {
  // Usar função batch para um único item (compatibilidade)
  const result = await updateObjectMetadataBatch(
    [{
      staticId,
      guid: guid!,
      multiplicador,
      contracao,
      baseName: nomeBase
    }],
    (batchResult) => {
      if (callback) {
        if (batchResult.sucesso && batchResult.updated > 0) {
          callback(batchResult.items, { 
            status: 'ok', 
            itens: batchResult.errors, 
            original: batchResult 
          });
        } else {
          callback([], { status: 'error', original: batchResult });
        }
      }
    }
  );
  
  return result.sucesso ? 'success' : null;
};

/**
 * Salva os dados dos objetos organizados no localStorage
 */
export const saveOrganizedObjects = (objects: OrganizedObject[]): boolean => {
  try {
    storage.set(Keys.corel.organizedObjects, objects, { area: 'session', ttlMs: 24 * 60 * 60 * 1000 });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Recupera os objetos organizados do localStorage
 */
export const getOrganizedObjects = (): OrganizedObject[] | null => {
  try {
    const data = readWithAliases<OrganizedObject[]>(Keys.corel.organizedObjects, ['corel_organized_objects'], { area: 'session', persistIfAliased: true });
    return data || null;
  } catch (error) {
    return null;
  }
};

/**
 * Normaliza um objeto retornado pela API
 */
export const normalizeOrganizedObject = (apiObject: ApiOrganizedObject): OrganizedObject => {
  return {
    nome: apiObject.Nome,
    guid: (apiObject as any).Guid || (apiObject as any).guid,
    staticId: (apiObject as any).StaticID || (apiObject as any).staticId, // aceitar PascalCase e camelCase
    tipo: apiObject.Tipo,
    largura: apiObject.Largura,
    altura: apiObject.Altura,
    posicaoX: apiObject.PosicaoX,
    posicaoY: apiObject.PosicaoY
  };
};

/**
 * Normaliza um array de objetos retornados pela API
 */
export const normalizeOrganizedObjects = (apiObjects: ApiOrganizedObject[]): OrganizedObject[] => {
  return apiObjects.map(normalizeOrganizedObject);
};

/**
 * Converte objetos organizados para o formato OrderObject
 */
export const convertToOrderObjects = (
  organizedObjects: OrganizedObject[] | ApiOrganizedObject[],
  unitFrom: Unit = 'mm'
): OrderObject[] => {
  // ✅ Log reduzido - apenas resumo
  
  const result = organizedObjects.map((obj, index) => {
    const nome = 'Nome' in obj ? obj.Nome : obj.nome;
    const staticId = 'StaticID' in obj ? obj.StaticID : obj.staticId;
    const guid = 'Guid' in obj ? (obj as any).Guid : (obj as any).guid;
    const larguraOrig = 'Largura' in obj ? obj.Largura : obj.largura;
    const alturaOrig = 'Altura' in obj ? obj.Altura : obj.altura;
    // Novos metadados vindos do C#: multiplicador/contracao (aceitar PascalCase e camelCase)
    const multiplicadorRaw = 'Multiplicador' in obj
      ? (obj as any).Multiplicador
      : (('multiplicador' in (obj as any)) ? (obj as any).multiplicador : undefined);
    const contracaoMeta = 'Contracao' in obj
      ? (obj as any).Contracao
      : (('contracao' in (obj as any)) ? (obj as any).contracao : undefined);
    
    // Converter para centímetros a partir da unidade de origem
    const larguraCM: number = toCm(larguraOrig, unitFrom);
    const alturaCM: number = toCm(alturaOrig, unitFrom);
    
    // ✅ Log removido - apenas resumo no final

    const areaMQ = (larguraCM * alturaCM) / 10000; // cm^2 -> m^2
    
    const multiplicadorNum = typeof multiplicadorRaw === 'number'
      ? multiplicadorRaw
      : (multiplicadorRaw != null && !Number.isNaN(Number(multiplicadorRaw)) ? Number(multiplicadorRaw) : undefined);

    // Metadados unificados vindos do C# (podem estar em camelCase/PascalCase)
    const pageIndex = ('Pagina' in (obj as any)) ? (obj as any).Pagina : (obj as any).pageIndex;
    const exportIndex = (obj as any)?.exportIndex ?? (obj as any)?.ExportIndex ?? (obj as any)?.meta?.exportIndex ?? null;

    const orderObj: OrderObject = {
      id: `obj-${index + 1}`,
      nome: nome,
      staticId: staticId, // ← NOVO: Incluir StaticID
      guid: guid,
      width: parseFloat(larguraCM.toFixed(2)),
      height: parseFloat(alturaCM.toFixed(2)),
      quantity: (typeof multiplicadorNum === 'number' && multiplicadorNum > 0) ? multiplicadorNum : 1,
      totalArea: parseFloat(areaMQ.toFixed(2)),
      status: 'success',
      contraction: contracaoMeta == null || contracaoMeta === '0' ? '0px' : String(contracaoMeta),
      pageIndex: typeof pageIndex === 'number' ? pageIndex : undefined,
      exportIndex: (exportIndex == null || Number.isNaN(Number(exportIndex))) ? null : Number(exportIndex)
    };
    return orderObj;
  });
  // Log detalhado removido após validação inicial – manter ambiente limpo
  // ✅ Log resumo apenas (reduzir verbosidade)
  if (result.length > 0) {
    sendToTerminal('✅ [CONVERT] Conversão concluída', {
      totalObjetos: result.length,
      unitFrom
    });
  }
  
  return result;
};

/**
 * Recupera os objetos no formato OrderObject
 */
export const getOrderObjects = async (
  _options?: { unitFrom?: Unit } // Mantido para compatibilidade futura
): Promise<OrderObject[] | null> => {
  // Usar MetadataService centralizado
  const unitFrom: Unit = 'mm';
  
  try {
    const result = await MetadataService.collect('document');
    
    if (!result.sucesso || !Array.isArray(result.objetos) || result.objetos.length === 0) {
      if (!result.sucesso) {
        sendToTerminal('❌ [META:COLLECT] Erro ao coletar metadados', {
          error: result.erro
        });
      }
      return null;
    }
    
    // Mapear objetos (collect) → OrderObject
    const mapped: OrderObject[] = result.objetos.map((o: any, idx: number) => {
      const vis = o?.visuais || {};
      const larguraCM: number = toCm(vis?.width ?? o?.largura ?? 0, unitFrom);
      const alturaCM: number = toCm(vis?.height ?? o?.altura ?? 0, unitFrom);
      return {
        id: `obj-${idx + 1}`,
        nome: o?.nome || '',
        staticId: o?.staticId || '',
        guid: o?.guid || null,
        width: parseFloat(larguraCM.toFixed(2)),
        height: parseFloat(alturaCM.toFixed(2)),
        quantity: Math.max(1, Number(o?.multiplicador ?? 1)),
        totalArea: parseFloat(((larguraCM * alturaCM) / 10000).toFixed(2)),
        status: 'success',
        contraction: o?.contracao == null || o?.contracao === '0' ? '0px' : String(o?.contracao),
        pageIndex: typeof vis?.page === 'number' ? vis.page : undefined,
        exportIndex: (o?.exportIndex == null || Number.isNaN(Number(o?.exportIndex))) ? null : Number(o?.exportIndex)
      } as OrderObject;
    });
    
    return mapped;
  } catch (error) {
    sendToTerminal('❌ [META:COLLECT] Exception', {
      error: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

/**
 * Configura o listener para mensagens do WebView2
 */
export const setupCorelListener = (
  callback: (response: WebViewResponse) => void
): (() => void) => {
  return corelCommunication.setupListener(callback);
};